import { useRef, useState } from 'react';
import { Archive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { nivelCaducidad } from '@shared/certificados';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AccionActivar } from '@/components/certificados/AccionActivar';
import { eliminarCertificadoApi } from '@/utils/certificadosApi';
import { formatearFecha } from '@/utils/avisoCertificado';
import type { DigitalCertificate } from '@/types';

interface Props {
  archivados: DigitalCertificate[];
  /** Se llama tras volver a un certificado o eliminarlo: el padre recarga y avisa a la app. */
  onCambio: () => void;
}

/** Historial: certificados archivados con la fecha (prevista o real) de borrado de su clave privada. */
export function HistorialCertificados({ archivados, onCambio }: Props) {
  const { fetchWithAuth } = useUser();
  const [eliminando, setEliminando] = useState<string | null>(null);
  const eliminandoRef = useRef(false);
  if (archivados.length === 0) return null;

  const eliminar = async (certificado: DigitalCertificate) => {
    if (eliminandoRef.current) return;
    if (!window.confirm(`¿Eliminar el certificado «${certificado.nombre}»?`)) return;
    eliminandoRef.current = true;
    setEliminando(certificado.id);
    let eliminado = false;
    try {
      const resultado = await eliminarCertificadoApi(fetchWithAuth, certificado.id);
      if (resultado.ok) {
        toast.success('Certificado eliminado.');
        eliminado = true;
      } else {
        toast.error(resultado.error);
      }
    } catch {
      toast.error('No se pudo eliminar el certificado.');
    } finally {
      eliminandoRef.current = false;
      setEliminando(null);
    }
    // Fuera del try: si el padre falla al recargar, el borrado ya se hizo y no hay que decir lo contrario.
    if (eliminado) {
      try {
        onCambio();
      } catch (error) {
        console.error('Error al recargar tras eliminar el certificado:', error);
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Archive className="h-5 w-5" aria-hidden="true" />
          Historial
        </CardTitle>
        <CardDescription>
          Certificados anteriores. La clave privada de cada uno se borra a los 30 días de archivarlo.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {archivados.map((certificado) => {
            const conservaClave = !certificado.clavePrivadaBorradaEn;
            const segundaParte = certificado.clavePrivadaBorradaEn ?
              `Clave privada borrada el ${formatearFecha(certificado.clavePrivadaBorradaEn)}` :
              certificado.fechaPrevistaLimpieza ?
                `Se borrará su clave privada el ${formatearFecha(certificado.fechaPrevistaLimpieza)}` :
                '';
            const caducado = nivelCaducidad(certificado.validUntil).nivel === 'caducado';
            return (
              <li key={certificado.id} className="space-y-2 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium">{certificado.nombre}</span>
                    <p className="text-sm text-muted-foreground">
                      {certificado.archivadoEn ? `Archivado el ${formatearFecha(certificado.archivadoEn)}` : 'Archivado'}
                      {segundaParte && ` · ${segundaParte}`}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    {conservaClave && !caducado && (
                      <AccionActivar certificadoId={certificado.id} etiqueta="Volver a este" onActivado={onCambio} />
                    )}
                    <Button variant="ghost" size="sm" onClick={() => eliminar(certificado)}
                      aria-label={`Eliminar ${certificado.nombre}`} disabled={eliminando !== null}>
                      <Trash2 className="mr-2 h-4 w-4 text-destructive" aria-hidden="true" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
